import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'

export function InputGroup({ children }: React.ComponentPropsWithoutRef<'span'>) {
    return (
        <span
            data-slot="control"
            className={clsx(
                'relative isolate block',
                '[&_input]:has-[[data-slot=icon]:first-child]:pl-10 [&_input]:has-[[data-slot=icon]:last-child]:pr-10 sm:[&_input]:has-[[data-slot=icon]:first-child]:pl-8 sm:[&_input]:has-[[data-slot=icon]:last-child]:pr-8',
                '[&>[data-slot=icon]]:pointer-events-none [&>[data-slot=icon]]:absolute [&>[data-slot=icon]]:top-3 [&>[data-slot=icon]]:z-10 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:top-2.5 sm:[&>[data-slot=icon]]:size-4',
                '[&>[data-slot=icon]:first-child]:left-3 sm:[&>[data-slot=icon]:first-child]:left-2.5 [&>[data-slot=icon]:last-child]:right-3 sm:[&>[data-slot=icon]:last-child]:right-2.5',
                '[&>[data-slot=icon]]:text-zinc-500 dark:[&>[data-slot=icon]]:text-zinc-400'
            )}
        >
            {children}
        </span>
    )
}

const dateTypes = ['date', 'datetime-local', 'month', 'time', 'week']
type DateType = (typeof dateTypes)[number]

export const Input = forwardRef(function Input(
    {
        className,
        ...props
    }: {
        className?: string
        type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | DateType
    } & Omit<Headless.InputProps, 'as' | 'className'>,
    ref: React.ForwardedRef<HTMLInputElement>
) {
    return (
        <span data-slot="control" className={clsx(className, 'relative block w-full')}>
            <Headless.Input
                ref={ref}
                {...props}
                className={clsx([
                    // Date classes
                    props.type &&
                    dateTypes.includes(props.type) && [
                        '[&::-webkit-datetime-edit-fields-wrapper]:p-0',
                        '[&::-webkit-date-and-time-value]:min-h-[1.5em]',
                        '[&::-webkit-datetime-edit]:inline-flex',
                        '[&::-webkit-datetime-edit]:p-0',
                        '[&::-webkit-datetime-edit-year-field]:p-0',
                        '[&::-webkit-datetime-edit-month-field]:p-0',
                        '[&::-webkit-datetime-edit-day-field]:p-0',
                        '[&::-webkit-datetime-edit-hour-field]:p-0',
                        '[&::-webkit-datetime-edit-minute-field]:p-0',
                        '[&::-webkit-datetime-edit-second-field]:p-0',
                        '[&::-webkit-datetime-edit-millisecond-field]:p-0',
                        '[&::-webkit-datetime-edit-meridiem-field]:p-0',
                    ],
                    // Basic layout — full pill, flat (No-Border Rule)
                    'relative block w-full appearance-none rounded-full px-[calc(theme(spacing.4)-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
                    // Typography
                    'text-base/6 text-foreground placeholder:text-muted-foreground sm:text-sm/6',
                    // No border — Surface Low fill at rest, Pure Surface + teal ring on focus
                    'border-none bg-secondary focus:bg-background',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                    // Invalid state
                    'data-[invalid]:ring-2 data-[invalid]:ring-destructive',
                    // Disabled state
                    'data-[disabled]:opacity-50',
                    // System icons
                    'dark:[color-scheme:dark]',
                ])}
            />
        </span>
    )
})
